/**
 * The self-audit bus — one place every system publishes its own invariants, and one
 * place the harness collects the violations.
 *
 * WHY THIS EXISTS. On every boot, `trails.ts` prints this:
 *
 *   [trails] BUILT SURFACE (1608 stations) — ... crest 9.996 1/m (budget 0.068,
 *   launches a coasting bike at 3.6 km/h), 13.3% over ...
 *
 * A system 147x over its own stated budget, measured by its own code, printed to the
 * console on every single boot — and shipped for four waves, because nobody reads
 * stdout. The number nobody had to go find was already there. Five waves have now
 * produced five measurement failures that each led to a WRONG conclusion; the common
 * factor is not that the numbers were hard to get, it is that a number sitting
 * somewhere nobody looks is the same as no number at all.
 *
 * So: a system declares what it is not allowed to do, reports observations against it,
 * and the capture harness lifts every violation to the TOP of `SUMMARY.md`. A critic
 * sees it without going looking. A piece cannot quietly ship 147x over its own budget.
 *
 * THREE THINGS THIS IS NOT:
 *  - Not a logger. Nothing here writes to the console unless you ask it to.
 *  - Not a profiler. It measures whatever number you hand it, in whatever unit you say.
 *  - Not a judge of whether your budget is right. It only holds you to the one you
 *    published. Publishing a budget you are 147x over is still visible — that is the
 *    point — but a budget nobody can defend is a different failure and this cannot see it.
 *
 * COST. `observe()` is a compare, two adds and a branch: no allocation, no string work,
 * no closure. It is safe in a 240 Hz fixed step and in a per-vertex build loop. The only
 * allocating calls are `declareInvariant()` (once, at module load) and `auditReport()`
 * (once, at the end of a run). Anything that takes a `where: string` is documented as
 * cold-path — the allocation is in the caller's template literal, not here.
 *
 * ---------------------------------------------------------------------------
 * QUICK START
 *
 *   import { declareInvariant } from '../core/audit';
 *
 *   // Module scope. Declared once, at import.
 *   const WHEEL_SINK = declareInvariant({
 *     id: 'ridefeel.wheelSinkDepth',
 *     owner: 'ride-feel',
 *     what: 'drawn wheel contact point below the terrain surface',
 *     unit: 'm',
 *     budget: 0.05,
 *     severity: 'fatal',
 *     phase: 'run',
 *     consequence: 'the bike is drawn inside the hill; screenshots show dirt to the waist',
 *   });
 *
 *   // Hot path. Zero allocation.
 *   WHEEL_SINK.observeAt(surfaceY - wheelY, wheelX, wheelY, wheelZ);
 *
 *   // Build path, when you would rather not ship the thing at all:
 *   CREST.assertClean();   // throws InvariantViolation if the batch broke the budget
 *
 * ---------------------------------------------------------------------------
 * VALIDATING THE INSTRUMENT. `auditSelfTest()` feeds the bus known-good AND known-bad
 * series and asserts it says so — the "check it can fail" step `docs/AGENTS.md` asks
 * for. The capture harness runs it on every capture and prints PASS/FAIL next to the
 * violations, so an audit block can never be read as authoritative without its own
 * instrument having been checked in the same run.
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * When the invariant is exercised.
 *
 * - `build`  — observed once while a system constructs something (trails carving its
 *              surface, scatter placing stems). Never reset between scenarios, because
 *              it is a property of the built world, not of a run.
 * - `run`    — observed while riding. Reset at the start of every scenario, so a
 *              per-scenario report is that scenario's, not a running total across the
 *              whole page session. (A cumulative clock read as a per-run one is
 *              literally wave-1's measurement bug; do not reintroduce it here.)
 */
export type AuditPhase = 'build' | 'run';

/**
 * How loud a violation is. This orders the report and nothing else — the bus never
 * suppresses anything.
 *
 * - `fatal` — this alone can break the ride-it gate.
 * - `error` — a real defect a player would notice.
 * - `warn`  — out of budget, but the budget is an aspiration rather than a promise.
 */
export type AuditSeverity = 'fatal' | 'error' | 'warn';

/**
 * Which side of the budget is legal.
 * - `max` — observations must be <= budget (a step height, a frame cost).
 * - `min` — observations must be >= budget (a clearance, a frame rate).
 * - `abs` — |observation| must be <= budget (a signed error either way).
 */
export type AuditCompare = 'max' | 'min' | 'abs';

export interface InvariantSpec {
  /** Stable, dotted, `owner.metricName`. Appears verbatim in SUMMARY.md — pick a name a
   *  critic can grep for and an owner can be found from. */
  id: string;
  /** The piece that owns the FIX, not the piece that noticed. This is who gets routed. */
  owner: string;
  /** One line, plain English, no units: "convex crest over the chord a 29in wheel bridges". */
  what: string;
  /** `m`, `mm`, `1/m`, `m/m`, `deg`, `ms`, `frac`, `count`... printed next to the number. */
  unit: string;
  /** The limit, in `unit`. Put the real constant here, not a rounded copy of it. */
  budget: number;
  /** Default `max`. */
  compare?: AuditCompare;
  /** Default `error`. */
  severity?: AuditSeverity;
  /** Default `run`. */
  phase?: AuditPhase;
  /** What a violation DOES to the rider or the frame. This is the sentence that makes a
   *  critic act, so write the consequence, not a restatement of the metric.
   *  Good: "launches a coasting bike at 3.6 km/h". Bad: "curvature is too high". */
  consequence?: string;
  /** Where the budget constant lives, for whoever wants to argue with it.
   *  e.g. `src/world/trails.ts KAPPA_CREST`. */
  source?: string;
}

/** One invariant's state, as handed to the harness. Plain data, JSON-safe. */
export interface InvariantReport {
  id: string;
  owner: string;
  what: string;
  unit: string;
  budget: number;
  compare: AuditCompare;
  severity: AuditSeverity;
  phase: AuditPhase;
  consequence: string | null;
  source: string | null;
  /** Observations recorded (including any folded in by `observeBatch`). */
  samples: number;
  /** How many of them broke the budget. */
  over: number;
  /** 100 * over / samples. */
  pctOver: number;
  /** The single worst observation, in `unit`. 0 when nothing was observed. */
  worst: number;
  /** worst / budget for `max`/`abs`, budget / worst for `min`. 1.0 is exactly on budget. */
  worstRatio: number;
  /** How far past the budget the worst observation was, in `unit`. Negative = headroom. */
  worstExcess: number;
  /** Mean of per-sample observations; null when only batches were folded in. */
  mean: number | null;
  /** Where the worst one was: whatever the caller passed, else a formatted position. */
  where: string | null;
  /** The scenario (or build phase) the worst observation happened in. */
  context: string | null;
  /** Observations that were not finite. Always a violation, and usually the real bug. */
  nonFinite: number;
  /** True if anything broke the budget, or anything was non-finite. */
  violated: boolean;
  /** How many times `assert`/`assertClean` refused to let something be published. */
  refusals: number;
  /** Preformatted so SUMMARY.md and any other consumer print the same digits. */
  worstText: string;
  budgetText: string;
  overText: string;
}

/** A system that refused to publish rather than ship over its own budget. */
export interface AuditRefusal {
  id: string;
  owner: string;
  message: string;
}

export interface AuditReport {
  /** Bump when the shape changes; the harness prints what it does not understand. */
  version: 1;
  available: true;
  /** No violations, no non-finite samples, no refusals, and the self-test passed. */
  ok: boolean;
  declared: number;
  /** Declared but never observed — an owner has not wired theirs up yet. */
  unexercised: string[];
  violated: number;
  worstId: string | null;
  worstRatio: number;
  refusals: AuditRefusal[];
  /** Violations first (fatal > error > warn, then by ratio), then the clean ones. */
  invariants: InvariantReport[];
  selfTest: SelfTestReport;
}

export interface SelfTestCheck { name: string; ok: boolean; detail: string }
export interface SelfTestReport { ok: boolean; checks: number; failures: SelfTestCheck[] }

/** Thrown by `assert` / `assertClean`. Catch it to publish a fallback instead. */
export class InvariantViolation extends Error {
  readonly id: string;
  readonly owner: string;
  readonly value: number;
  readonly budget: number;
  readonly unit: string;
  readonly where: string | null;
  constructor(inv: Invariant, value: number, where: string | null, detail: string) {
    super(`[audit] ${inv.id} (${inv.owner}) REFUSED: ${detail}`);
    this.name = 'InvariantViolation';
    this.id = inv.id;
    this.owner = inv.owner;
    this.value = value;
    this.budget = inv.budget;
    this.unit = inv.unit;
    this.where = where;
  }
}

// ---------------------------------------------------------------------------
// Number formatting — one implementation, so every channel prints the same digits.
// ---------------------------------------------------------------------------

/** Significant-figure formatting that survives 1e-4 and 1e4 without going to junk. */
export function fmtAuditNum(v: number): string {
  if (!Number.isFinite(v)) return v > 0 ? 'Inf' : Number.isNaN(v) ? 'NaN' : '-Inf';
  const a = Math.abs(v);
  if (a === 0) return '0';
  if (a >= 1000) return v.toFixed(0);
  if (a >= 100) return v.toFixed(1);
  if (a >= 1) return v.toFixed(3);
  if (a >= 0.01) return v.toFixed(4);
  return v.toExponential(2);
}

// ---------------------------------------------------------------------------
// The invariant
// ---------------------------------------------------------------------------

/**
 * The "nothing observed yet" worst, per compare mode.
 *
 * `abs` is the one that is not obvious, and getting it wrong is the first defect the
 * bus's own Node probe caught: seeded at -Infinity, `isWorse` compares |v| against
 * |-Infinity| = Infinity, so an `abs` invariant counts its violations correctly and
 * then reports `worst 0` with no location — a metric that fires and cannot say where.
 * Exactly the shape of failure this module exists to stop, found by feeding the
 * instrument a case it was known to have to catch.
 */
function emptyWorst(compare: AuditCompare): number {
  if (compare === 'min') return Infinity;
  if (compare === 'abs') return 0;
  return -Infinity;
}

/**
 * A live invariant handle. Hold the one `declareInvariant` returned you at module scope
 * and call `observe*` on it; do not look it up by id per frame.
 */
export class Invariant {
  readonly id: string;
  readonly owner: string;
  readonly what: string;
  readonly unit: string;
  readonly compare: AuditCompare;
  readonly severity: AuditSeverity;
  readonly phase: AuditPhase;
  readonly consequence: string | null;
  readonly source: string | null;
  /** Mutable so an owner re-declaring with their own constant wins over a placeholder. */
  budget: number;

  // --- counters. Plain numbers on purpose: observe() must not allocate. -------------
  samples = 0;
  over = 0;
  nonFinite = 0;
  refusals = 0;
  private worstV: number;
  private sum = 0;
  private sumN = 0;
  private whereStr: string | null = null;
  private hasPos = false;
  private px = 0;
  private py = 0;
  private pz = 0;
  private ctx: string | null = null;

  /** @internal — use `declareInvariant`. Public only so `auditSelfTest` can build a
   *  scratch instance that never enters the registry. */
  constructor(spec: InvariantSpec) {
    this.id = spec.id;
    this.owner = spec.owner;
    this.what = spec.what;
    this.unit = spec.unit;
    this.budget = spec.budget;
    this.compare = spec.compare ?? 'max';
    this.severity = spec.severity ?? 'error';
    this.phase = spec.phase ?? 'run';
    this.consequence = spec.consequence ?? null;
    this.source = spec.source ?? null;
    this.worstV = emptyWorst(this.compare);
  }

  // --- the hot path ----------------------------------------------------------------

  /**
   * Record one observation. Zero allocation; safe at 240 Hz.
   *
   * The value is in the unit you declared. If you have a position or a label for it,
   * prefer `observeAt` / `observeWhere` — "worst 2.05 m" with no location has cost this
   * project a round before.
   */
  observe(v: number): void {
    if (!Number.isFinite(v)) { this.nonFinite++; this.samples++; return; }
    this.samples++;
    this.sum += v;
    this.sumN++;
    if (this.isOver(v)) this.over++;
    if (this.isWorse(v)) {
      this.worstV = v;
      this.whereStr = null;
      this.hasPos = false;
      this.ctx = context;
    }
  }

  /**
   * Record one observation with the world position it happened at. Zero allocation —
   * the coordinates are stored as three numbers and only formatted if this ends up
   * being the worst one, at report time.
   */
  observeAt(v: number, x: number, y: number, z: number): void {
    if (!Number.isFinite(v)) { this.nonFinite++; this.samples++; return; }
    this.samples++;
    this.sum += v;
    this.sumN++;
    if (this.isOver(v)) this.over++;
    if (this.isWorse(v)) {
      this.worstV = v;
      this.whereStr = null;
      this.hasPos = true;
      this.px = x; this.py = y; this.pz = z;
      this.ctx = context;
    }
  }

  /**
   * Record one observation with a caller-supplied label.
   *
   * COLD PATH ONLY unless the string is interned. Building `` `${run}@${s}m` `` per
   * sample allocates in the CALLER, which is fine in a build loop that runs once and
   * is not fine in `fixedUpdate`. In a hot loop use `observeAt`, or `observe` plus a
   * single `noteWorst()` after the loop.
   */
  observeWhere(v: number, where: string): void {
    if (!Number.isFinite(v)) { this.nonFinite++; this.samples++; return; }
    this.samples++;
    this.sum += v;
    this.sumN++;
    if (this.isOver(v)) this.over++;
    if (this.isWorse(v)) {
      this.worstV = v;
      this.whereStr = where;
      this.hasPos = false;
      this.ctx = context;
    }
  }

  /**
   * Fold in a batch a system already counted itself.
   *
   * This is the migration path for code that has its own audit loop and its own
   * worst/percentage bookkeeping (trails' `auditCrossSection` is exactly this) — it
   * publishes without re-instrumenting the inner loop.
   *
   * `worst` and `over` must be measured against the SAME threshold, or the report says
   * "worst 10.28, 45.1% over" about two different metrics and a reader will join them.
   * That mismatch is currently live in the trails boot line; see the migration note in
   * the wave-5 report.
   */
  observeBatch(worst: number, samples: number, over: number, where?: string): void {
    if (samples > 0) this.samples += samples;
    if (over > 0) this.over += over;
    if (!Number.isFinite(worst)) { this.nonFinite++; return; }
    if (this.isWorse(worst)) {
      this.worstV = worst;
      this.whereStr = where ?? null;
      this.hasPos = false;
      this.ctx = context;
    }
  }

  /** Attach a label to the current worst observation, after a hot loop that used
   *  `observe()`. No-op if nothing has been observed. */
  noteWorst(where: string): void {
    if (this.samples > 0) this.whereStr = where;
  }

  // --- predicates and gates --------------------------------------------------------

  /** Is this value within budget? Records nothing. Use to branch without polluting stats. */
  check(v: number): boolean {
    return Number.isFinite(v) && !this.isOver(v);
  }

  /**
   * Record `v`, and THROW if it broke the budget.
   *
   * This is the "refuse to publish" call: a system that would rather not exist than
   * exist out of spec calls this on the value it is about to commit. Returns `v` so it
   * can wrap an expression: `const h = CREST.assert(computeCrest(), where);`
   */
  assert(v: number, where?: string): number {
    if (where !== undefined) this.observeWhere(v, where); else this.observe(v);
    if (Number.isFinite(v) && !this.isOver(v)) return v;
    const dir = this.compare === 'min' ? 'below' : 'over';
    throw this.refuse(
      v, where ?? null,
      `${fmtAuditNum(v)} ${this.unit} is ${dir} the budget of ${fmtAuditNum(this.budget)} ${this.unit}`
      + `${where ? ` at ${where}` : ''} — ${this.what}`,
    );
  }

  /**
   * THROW if anything observed so far broke the budget.
   *
   * The batch form of `assert`: run your build, observe every sample, then refuse to
   * publish the whole artefact if the batch is out of spec. `maxPctOver` lets an owner
   * ratchet — set it to today's number and lower it, rather than either shipping
   * silently or refusing to boot.
   */
  assertClean(maxPctOver = 0): void {
    if (this.nonFinite === 0 && this.pctOver <= maxPctOver) return;
    const nf = this.nonFinite > 0 ? `${this.nonFinite} non-finite samples; ` : '';
    throw this.refuse(
      this.worst, this.where,
      `${nf}${this.pctOver.toFixed(1)}% of ${this.samples} samples over budget `
      + `(allowed ${maxPctOver}%), worst ${fmtAuditNum(this.worst)} ${this.unit} `
      + `against ${fmtAuditNum(this.budget)}${this.where ? ` at ${this.where}` : ''}`,
    );
  }

  /**
   * Record that this system refused to publish, WITHOUT throwing — for a builder that
   * catches its own gate and substitutes a fallback. The refusal still reaches the top
   * of SUMMARY.md, which is the point: a fallback that nobody is told about is how a
   * defect survives four waves.
   */
  recordRefusal(message: string): void {
    this.refusals++;
    refusals.push({ id: this.id, owner: this.owner, message });
  }

  /** Book the refusal (so it reaches SUMMARY.md with its real message) and build the
   *  error. Callers `throw` the return value. */
  private refuse(v: number, where: string | null, detail: string): InvariantViolation {
    const err = new InvariantViolation(this, v, where, detail);
    this.refusals++;
    refusals.push({ id: this.id, owner: this.owner, message: detail });
    return err;
  }

  // --- derived state ---------------------------------------------------------------

  get worst(): number {
    return Number.isFinite(this.worstV) ? this.worstV : 0;
  }

  get pctOver(): number {
    return this.samples > 0 ? (100 * this.over) / this.samples : 0;
  }

  get violated(): boolean {
    return this.over > 0 || this.nonFinite > 0;
  }

  get where(): string | null {
    if (this.whereStr) return this.whereStr;
    if (!this.hasPos) return null;
    return `(${this.px.toFixed(1)}, ${this.py.toFixed(1)}, ${this.pz.toFixed(1)})`;
  }

  /** worst/budget (`max`,`abs`) or budget/worst (`min`). 1.0 is exactly on budget. */
  get worstRatio(): number {
    if (this.samples === 0) return 0;
    const w = this.compare === 'abs' ? Math.abs(this.worst) : this.worst;
    if (this.compare === 'min') {
      if (w === 0) return Infinity;
      return this.budget / w;
    }
    if (this.budget === 0) return w > 0 ? Infinity : 0;
    return w / Math.abs(this.budget);
  }

  /** Reset counters. Called on run-phase invariants at every scenario boundary. */
  reset(): void {
    this.samples = 0;
    this.over = 0;
    this.nonFinite = 0;
    this.refusals = 0;
    this.sum = 0;
    this.sumN = 0;
    this.whereStr = null;
    this.hasPos = false;
    this.ctx = null;
    this.worstV = emptyWorst(this.compare);
  }

  /** Allocating. Report time only. */
  snapshot(): InvariantReport {
    const w = this.compare === 'abs' ? Math.abs(this.worst) : this.worst;
    const excess = this.compare === 'min' ? this.budget - w : w - this.budget;
    const ratio = this.worstRatio;
    return {
      id: this.id,
      owner: this.owner,
      what: this.what,
      unit: this.unit,
      budget: this.budget,
      compare: this.compare,
      severity: this.severity,
      phase: this.phase,
      consequence: this.consequence,
      source: this.source,
      samples: this.samples,
      over: this.over,
      pctOver: +this.pctOver.toFixed(3),
      worst: this.worst,
      worstRatio: +ratio.toFixed(3),
      worstExcess: this.samples > 0 ? excess : 0,
      mean: this.sumN > 0 ? this.sum / this.sumN : null,
      where: this.where,
      context: this.ctx,
      nonFinite: this.nonFinite,
      violated: this.violated,
      refusals: this.refusals,
      worstText: this.samples > 0 ? fmtAuditNum(this.worst) : '-',
      budgetText: fmtAuditNum(this.budget),
      overText: this.samples > 0 && Number.isFinite(ratio) ? `${ratio.toFixed(1)}x` : '-',
    };
  }

  // --- internals -------------------------------------------------------------------

  private isOver(v: number): boolean {
    if (this.compare === 'min') return v < this.budget;
    if (this.compare === 'abs') return (v < 0 ? -v : v) > this.budget;
    return v > this.budget;
  }

  private isWorse(v: number): boolean {
    if (this.compare === 'min') return v < this.worstV;
    if (this.compare === 'abs') return (v < 0 ? -v : v) > (this.worstV < 0 ? -this.worstV : this.worstV);
    return v > this.worstV;
  }
}

// ---------------------------------------------------------------------------
// Registry
// ---------------------------------------------------------------------------

const registry = new Map<string, Invariant>();
const refusals: AuditRefusal[] = [];
/** The scenario currently being captured, stamped onto every worst observation. */
let context: string | null = null;

/**
 * Declare an invariant. Call at module scope, keep the handle, observe against it.
 *
 * Re-declaring an id returns the EXISTING handle (so stats survive) and adopts the new
 * spec's `budget`. That is deliberate: `audit.ts` pre-registers a few invariants with
 * placeholder budgets copied out of other people's files, and when the real owner
 * declares theirs with their own constant, the owner's number must win.
 */
export function declareInvariant(spec: InvariantSpec): Invariant {
  const existing = registry.get(spec.id);
  if (existing) {
    if (existing.budget !== spec.budget) existing.budget = spec.budget;
    return existing;
  }
  const inv = new Invariant(spec);
  registry.set(spec.id, inv);
  return inv;
}

export function getInvariant(id: string): Invariant | undefined {
  return registry.get(id);
}

export function listInvariants(): readonly Invariant[] {
  return [...registry.values()];
}

/** Label every subsequent worst observation with the scenario it came from. */
export function setAuditContext(name: string | null): void {
  context = name;
}

/** Reset run-phase invariants only. Build-phase state is a property of the built world
 *  and survives — it is measured once, at boot, and never re-measured. */
export function resetRunInvariants(): void {
  for (const inv of registry.values()) if (inv.phase === 'run') inv.reset();
  for (let i = refusals.length - 1; i >= 0; i--) {
    const owner = registry.get(refusals[i].id);
    if (owner && owner.phase === 'run') refusals.splice(i, 1);
  }
}

export function resetAllInvariants(): void {
  for (const inv of registry.values()) inv.reset();
  refusals.length = 0;
}

const SEV_RANK: Record<AuditSeverity, number> = { fatal: 0, error: 1, warn: 2 };

/** Collect everything. Allocating — call once, at the end of a run. */
export function auditReport(): AuditReport {
  const invs = [...registry.values()].map((i) => i.snapshot());
  invs.sort((a, b) => {
    if (a.violated !== b.violated) return a.violated ? -1 : 1;
    if (a.violated) {
      const s = SEV_RANK[a.severity] - SEV_RANK[b.severity];
      if (s !== 0) return s;
    }
    return b.worstRatio - a.worstRatio;
  });
  const violated = invs.filter((i) => i.violated);
  const all: AuditRefusal[] = refusals.slice();
  for (const i of invs) {
    if (i.refusals > 0 && !all.some((r) => r.id === i.id)) {
      all.push({
        id: i.id,
        owner: i.owner,
        message: `${i.refusals} refusal(s); worst ${i.worstText} ${i.unit} against ${i.budgetText}`,
      });
    }
  }
  const selfTest = auditSelfTest();
  const worst = violated.length > 0 ? violated[0] : null;
  return {
    version: 1,
    available: true,
    ok: violated.length === 0 && all.length === 0 && selfTest.ok,
    declared: invs.length,
    unexercised: invs.filter((i) => i.samples === 0).map((i) => i.id),
    violated: violated.length,
    worstId: worst ? worst.id : null,
    worstRatio: worst ? worst.worstRatio : 0,
    refusals: all,
    invariants: invs,
    selfTest,
  };
}

/** One line per invariant, for anyone who wants the audit in the console. Uses
 *  `console.info`, never `warn`/`error`, so it cannot pollute the harness's console
 *  section — SUMMARY.md is the channel that matters. */
export function formatAuditLines(): string[] {
  const r = auditReport();
  const out: string[] = [];
  out.push(
    r.violated > 0
      ? `[audit] ${r.violated} of ${r.declared} invariants OVER BUDGET`
      : `[audit] ${r.declared} invariants, all within budget`,
  );
  for (const i of r.invariants) {
    if (!i.violated) continue;
    out.push(
      `[audit] ${i.severity.toUpperCase()} ${i.id} (${i.owner}): worst ${i.worstText} ${i.unit} `
      + `vs budget ${i.budgetText} (${i.overText}), ${i.pctOver.toFixed(1)}% of ${i.samples} samples`
      + `${i.where ? ` — worst at ${i.where}` : ''}`
      + `${i.consequence ? ` — ${i.consequence}` : ''}`,
    );
  }
  return out;
}

export function logAudit(): void {
  for (const l of formatAuditLines()) console.info(l);
}

// ---------------------------------------------------------------------------
// Instrument self-test
//
// Five waves, five instruments that produced a confident wrong number. The rule in
// docs/AGENTS.md is: before you certify anything with an instrument, feed it a case you
// know is broken and confirm it says so. This does that to the bus itself, on a scratch
// invariant that never enters the registry, so it can run on every capture without
// leaving residue in the report it is validating.
// ---------------------------------------------------------------------------

export function auditSelfTest(): SelfTestReport {
  const failures: SelfTestCheck[] = [];
  let checks = 0;
  const expect = (name: string, ok: boolean, detail: string): void => {
    checks++;
    if (!ok) failures.push({ name, ok, detail });
  };
  const near = (a: number, b: number): boolean => Math.abs(a - b) < 1e-9;
  const scratch = (over: Partial<InvariantSpec> = {}): Invariant => new Invariant({
    id: '__selftest', owner: '__selftest', what: 'self test', unit: 'u', budget: 1, ...over,
  });

  // 1. A clean series must read clean. (An instrument that always fires is as useless
  //    as one that never does.)
  {
    const t = scratch();
    for (let i = 0; i < 10; i++) t.observe(0.5);
    expect('clean series is clean', !t.violated && t.over === 0 && t.samples === 10,
      `violated=${t.violated} over=${t.over} samples=${t.samples}`);
    expect('clean series worst', near(t.worst, 0.5), `worst=${t.worst}`);
    expect('clean series ratio', near(t.worstRatio, 0.5), `ratio=${t.worstRatio}`);
  }

  // 2. CAN IT FAIL? A series with 3 of 10 over budget must report exactly that.
  {
    const t = scratch();
    for (let i = 0; i < 7; i++) t.observe(0.5);
    t.observeWhere(1.5, 'a'); t.observeWhere(4, 'b'); t.observeWhere(1.2, 'c');
    expect('bad series is flagged', t.violated, `violated=${t.violated}`);
    expect('bad series over count', t.over === 3, `over=${t.over}`);
    expect('bad series pct', near(+t.pctOver.toFixed(6), 30), `pct=${t.pctOver}`);
    expect('bad series worst value', near(t.worst, 4), `worst=${t.worst}`);
    expect('bad series worst where', t.where === 'b', `where=${t.where}`);
    expect('bad series ratio', near(t.worstRatio, 4), `ratio=${t.worstRatio}`);
  }

  // 3. Boundary: exactly on budget is legal, a hair over is not.
  {
    const t = scratch();
    t.observe(1);
    expect('on budget is legal', t.over === 0, `over=${t.over}`);
    t.observe(1 + 1e-12);
    expect('a hair over is a violation', t.over === 1, `over=${t.over}`);
  }

  // 4. `min` and `abs` compare the right way round.
  {
    const lo = scratch({ compare: 'min', budget: 10 });
    lo.observe(12); lo.observe(4);
    expect('min: below budget is a violation', lo.over === 1 && near(lo.worst, 4),
      `over=${lo.over} worst=${lo.worst}`);
    expect('min: ratio is shortfall', near(lo.worstRatio, 2.5), `ratio=${lo.worstRatio}`);
    const ab = scratch({ compare: 'abs', budget: 1 });
    ab.observe(0.4); ab.observe(-3);
    expect('abs: negative excursion is a violation', ab.over === 1 && near(ab.worst, -3),
      `over=${ab.over} worst=${ab.worst}`);
    expect('abs: ratio uses magnitude', near(ab.worstRatio, 3), `ratio=${ab.worstRatio}`);
  }

  // 5. Non-finite is a violation, not a silent poisoning of `worst`.
  //    (This is the class of bug that ships as "max travel 170 mm" forever.)
  {
    const t = scratch();
    t.observe(0.5); t.observe(NaN); t.observe(Infinity);
    expect('non-finite counted', t.nonFinite === 2, `nonFinite=${t.nonFinite}`);
    expect('non-finite violates', t.violated, `violated=${t.violated}`);
    expect('non-finite does not become worst', near(t.worst, 0.5), `worst=${t.worst}`);
  }

  // 6. `assert` actually throws, and `check` never records.
  {
    const t = scratch();
    let threw = false;
    try { t.assert(9, 'boom'); } catch (e) { threw = e instanceof InvariantViolation; }
    expect('assert throws on violation', threw, `threw=${threw}`);
    expect('assert records the sample', t.samples === 1 && t.over === 1,
      `samples=${t.samples} over=${t.over}`);
    const c = scratch();
    c.check(99);
    expect('check records nothing', c.samples === 0, `samples=${c.samples}`);
  }

  // 7. `assertClean` refuses a dirty batch and passes a clean one.
  {
    const dirty = scratch();
    for (let i = 0; i < 9; i++) dirty.observe(0.1);
    dirty.observe(5);
    let threw = false;
    try { dirty.assertClean(); } catch { threw = true; }
    expect('assertClean refuses a dirty batch', threw, `threw=${threw}`);
    let threw2 = false;
    try { dirty.assertClean(10); } catch { threw2 = true; }
    expect('assertClean honours a ratchet', !threw2, `threw=${threw2}`);
  }

  // 8. Batches fold in with their own counts.
  {
    const t = scratch();
    t.observeBatch(3, 1000, 250, 'somewhere');
    expect('batch samples', t.samples === 1000 && t.over === 250,
      `samples=${t.samples} over=${t.over}`);
    expect('batch pct', near(t.pctOver, 25), `pct=${t.pctOver}`);
    expect('batch worst', near(t.worst, 3) && t.where === 'somewhere',
      `worst=${t.worst} where=${t.where}`);
  }

  // 9. Reset returns to a clean slate (per-scenario isolation depends on this).
  {
    const t = scratch();
    t.observe(50);
    t.reset();
    expect('reset clears', !t.violated && t.samples === 0 && t.worst === 0,
      `violated=${t.violated} samples=${t.samples} worst=${t.worst}`);
  }

  // 10. Position capture survives to the report.
  {
    const t = scratch();
    t.observeAt(4, -812.35, 241.04, 618.19);
    expect('observeAt formats a position', t.where === '(-812.4, 241.0, 618.2)',
      `where=${t.where}`);
  }

  return { ok: failures.length === 0, checks, failures };
}

// ---------------------------------------------------------------------------
// Pre-registered invariants
//
// These are the trails boot audit's four numbers, declared here so the bus already
// knows their names, owners, units and budgets before the trails owner touches
// anything. `trails.ts` is not edited by this piece; the four `observeBatch` calls that
// light these up are in the wave-5 report, ready to paste.
//
// Two notes for whoever pastes them:
//
//  - Budgets here are COPIES, taken from `src/world/trails.ts` at wave 5. Copies go
//    stale — that is instrument failure #1 in docs/AGENTS.md. Re-declaring an id with
//    the live constant replaces the budget and keeps everything else, so the paste
//    should re-declare rather than look up.
//  - The shipped boot line reports a worst and a percentage measured against DIFFERENT
//    thresholds (`shoulder worst lateral 10.28 ... 45.1% over 12 cm`: the worst is a
//    slope in m/m, the percentage counts 12 cm steps). Read together — which is how a
//    sentence is read — that is a wrong number. These four are declared in the unit the
//    percentage is actually counted in, so worst and % describe one thing.
// ---------------------------------------------------------------------------

export const TRAIL_INVARIANTS = {
  /** Inside the rideable shoulder: tread edge, drainage strip, catchment bench. */
  shoulderStep: declareInvariant({
    id: 'trails.shoulderStep',
    owner: 'trails',
    what: 'lateral height step across 6 cm of ground inside the rideable shoulder',
    unit: 'm',
    budget: 0.05,
    severity: 'error',
    phase: 'build',
    source: 'src/world/trails.ts auditCrossSection (step > 0.05 inside inB)',
    consequence: 'a step a wheel cannot ride up deflects the front and puts the bike off the line',
  }),
  /** Out on the batter, where CUT_SLOPE is the design and the budget is looser. */
  batterStep: declareInvariant({
    id: 'trails.batterStep',
    owner: 'trails',
    what: 'lateral height step across 6 cm of ground out on the cut/fill batter',
    unit: 'm',
    budget: 0.12,
    severity: 'warn',
    phase: 'build',
    source: 'src/world/trails.ts auditCrossSection (step > 0.12 outside inB)',
    consequence: 'the bank a rider bails onto is a staircase rather than a slope',
  }),
  /** The 147x number. */
  crestCurvature: declareInvariant({
    id: 'trails.crestCurvature',
    owner: 'trails',
    what: 'convex crest curvature over the 0.35 m chord a 29in wheel bridges',
    unit: '1/m',
    budget: 0.068,
    severity: 'fatal',
    phase: 'build',
    source: 'src/world/trails.ts KAPPA_CREST = 9.81 / V_HOLD^2',
    consequence: 'a crest of k launches a coasting bike at sqrt(g/k) — at 9.996 1/m that is 3.6 km/h,'
      + ' i.e. the trail throws the rider off itself at walking pace',
  }),
  /** Two independently graded runs carving one square metre to different heights. */
  ownershipStep: declareInvariant({
    id: 'trails.ownershipStep',
    owner: 'trails',
    what: 'vertical step across a run-to-run ownership boundary inside the corridor',
    unit: 'm',
    budget: 0.05,
    severity: 'fatal',
    phase: 'build',
    source: 'src/world/trails.ts auditCrossSection (step > 0.05 across an owner change)',
    consequence: 'a cliff down the middle of the trail where two lines disagree about the ground',
  }),
};

// ---------------------------------------------------------------------------
// Harness hook
//
// The capture harness reads this. It is installed at module import, which is BEFORE
// capture.mjs snapshots `__`-prefixed globals, so the per-scenario reset treats it as
// engine furniture and never deletes it.
// ---------------------------------------------------------------------------

export interface GripAuditHook {
  report(): AuditReport;
  selfTest(): SelfTestReport;
  resetRun(): void;
  resetAll(): void;
  setContext(name: string | null): void;
  list(): string[];
  get(id: string): InvariantReport | null;
  log(): void;
}

declare global {
  interface Window {
    __gripAudit?: GripAuditHook;
  }
}

if (typeof window !== 'undefined') {
  window.__gripAudit = {
    report: auditReport,
    selfTest: auditSelfTest,
    resetRun: resetRunInvariants,
    resetAll: resetAllInvariants,
    setContext: setAuditContext,
    list: () => [...registry.keys()],
    get: (id: string) => registry.get(id)?.snapshot() ?? null,
    log: logAudit,
  };
}
